import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Description as ReportIcon,
  InfoOutlined as InfoIcon,
  Lock as LockIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { toneOf } from 'components/admin-organizations/wellbeingTones';
import { WELLBEING_REPORTS, WELLBEING_REPORT_URL } from 'api/graphql/wellbeing';

// El registro del reporte lo crea la ruta firmada del PDF al descargarse, no la consulta que
// devuelve la URL: por eso la lista se refresca un momento después de abrirlo.
const REFRESH_DELAY = 3000;

const fmtDateTime = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function ReportMeta({ label, value, color = 'text.primary' }) {
  return (
    <Grid item xs={6} sm={3}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color }}>
        {value}
      </Typography>
    </Grid>
  );
}
ReportMeta.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired, color: PropTypes.string };
/**
 * Bienestar Corporativo (admin/RRHH) — histórico de reportes: cada PDF generado queda anotado
 * con su ventana, el índice de ese momento y la participación. Los que salieron protegidos por
 * k-anonymity se marcan con candado en vez de mostrar un puntaje que no existió.
 */
function OrganizationReportsTab({ organizationId }) {
  const { t } = useTranslation(['organizations']);
  const [weeks, setWeeks] = useState(4);
  const refreshTimer = useRef(null);

  const { data, loading, refetch } = useQuery(WELLBEING_REPORTS, {
    variables: { organization_id: organizationId },
    fetchPolicy: 'cache-and-network'
  });
  const [fetchReportUrl, { loading: generating }] = useLazyQuery(WELLBEING_REPORT_URL, { fetchPolicy: 'network-only' });

  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  const reports = data?.wellbeingReports || [];

  const handleGenerate = async () => {
    const { data: urlData } = await fetchReportUrl({ variables: { organization_id: organizationId, weeks } });
    const url = urlData?.organizationWellbeingReportUrl;
    if (!url) return;
    window.open(url, '_blank', 'noopener');
    refreshTimer.current = setTimeout(() => refetch(), REFRESH_DELAY);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            select
            size="small"
            label={t('wellbeingWindow', { defaultValue: 'Ventana' })}
            value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
            sx={{ minWidth: 140 }}>
            {[4, 8, 12].map((w) => (
              <MenuItem key={w} value={w}>
                {t('weeksN', { n: w, defaultValue: '{{n}} semanas' })}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={t('refreshList', { defaultValue: 'Actualizar lista' })}>
            <IconButton size="small" onClick={() => refetch()} aria-label={t('refreshList', { defaultValue: 'Actualizar lista' })} sx={{ color: 'text.secondary' }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Button
          startIcon={<PdfIcon />}
          onClick={handleGenerate}
          disabled={generating}
          sx={{ ...premiumButtonSx, py: 0, height: 40, borderRadius: 2, fontSize: '0.9rem', width: { xs: '100%', sm: 220 } }}>
          {t('generateNow', { defaultValue: 'Generar ahora' })}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2.5, p: 1.75, borderRadius: '14px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
        <InfoIcon sx={{ fontSize: 17, color: 'gold.main', flex: 'none', mt: '2px' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('reportsNote', {
            defaultValue: 'El check-in se abre cada lunes y ese mismo día se revisan las alertas tempranas. El reporte se arma cuando lo pides aquí, con la ventana de semanas que elijas, y cada descarga queda anotada abajo. Todo lo que sale en el PDF es agregado: si un grupo tiene menos de 5 personas respondiendo, su puntaje se protege.'
          })}
        </Typography>
      </Stack>

      {loading && reports.length === 0 && (
        <Stack spacing={1.5}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: '16px' }} />
          ))}
        </Stack>
      )}

      {!loading && reports.length === 0 && (
        <Stack
          spacing={1}
          alignItems="center"
          sx={{ py: 7, px: 2, textAlign: 'center', border: '1px dashed', borderColor: 'hairline.main', borderRadius: 3 }}>
          <ReportIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography variant="body2" fontWeight={700}>
            {t('reportsEmpty', { defaultValue: 'Todavía no has generado ningún reporte.' })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 420 }}>
            {t('reportsEmptyHelp', {
              defaultValue: 'Genera el primero con el botón de arriba: quedará guardado aquí con su índice y su participación.'
            })}
          </Typography>
        </Stack>
      )}

      <Stack spacing={1.5}>
        {reports.map((report) => (
          <PremiumCard key={report.id} padding={{ xs: 2, md: 2.5 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {fmtDateTime(report.created_at)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                  {report.generated_by_name || t('reportBySystem', { defaultValue: 'Sistema' })}
                  {report.week_key ? ` · ${report.week_key}` : ''}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={report.trigger === 'manual'
                  ? t('triggerManual', { defaultValue: 'Manual' })
                  : t('triggerScheduled', { defaultValue: 'Programado' })}
                sx={{
                  flex: 'none',
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  backgroundColor: report.trigger === 'manual' ? 'gold.soft' : 'surface.overlay',
                  color: report.trigger === 'manual' ? 'var(--otx-gold-text)' : 'text.secondary',
                  border: '1px solid',
                  borderColor: 'hairline.main'
                }}
              />
            </Stack>

            <Grid container spacing={1.5}>
              <ReportMeta
                label={t('reportWindow', { defaultValue: 'Ventana' })}
                value={t('weeksN', { n: report.weeks, defaultValue: '{{n}} semanas' })}
              />
              <ReportMeta
                label={t('wellbeingIndexShort', { defaultValue: 'Índice' })}
                value={report.was_protected ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700 }}>
                      {t('wellbeingProtectedShort', { defaultValue: 'Protegido' })}
                    </Typography>
                  </Stack>
                ) : (report.score ?? '—')}
                color={report.was_protected ? 'text.disabled' : toneOf(report.score)}
              />
              <ReportMeta
                label={t('wellbeingParticipationShort', { defaultValue: 'Participación' })}
                value={`${Math.round(report.participation_percent)}%`}
              />
              <ReportMeta
                label={t('reportRespondents', { defaultValue: 'Respondieron' })}
                value={`${report.respondents}/${report.members}`}
              />
            </Grid>
          </PremiumCard>
        ))}
      </Stack>
    </Box>
  );
}

OrganizationReportsTab.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default OrganizationReportsTab;
